(() => {
  const ROOT = window.Shopify?.routes?.root ? Shopify.routes.root : '/';

  const qs = (s, el = document) => el.querySelector(s);

  const isUsablePropertyValue = (v) => {
    if (v == null || v === '') return false;
    if (typeof File !== 'undefined' && v instanceof File) return v.size > 0 && !!v.name;
    return true;
  };

  const formatMoney = (cents) => {
    const currency = String(window.Shopify?.currency?.active || 'USD').toUpperCase();
    const locale = document.documentElement.lang || 'en-US';
    const value = (Number(cents) || 0) / 100;
    const whole = Number.isInteger(value);
    if (/^(USD|CAD|AUD|NZD|HKD|SGD|MXN)$/.test(currency)) {
      return '$' + (whole ? String(Math.round(value)) : value.toFixed(2));
    }
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2
      }).format(value);
    } catch (_) {
      return (whole ? String(Math.round(value)) : value.toFixed(2)) + ' ' + currency;
    }
  };

  window.aspFormatMoney = formatMoney;

  const addCartItems = async (items) => {
    const formData = new FormData();
    items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, String(item.id));
      formData.append(`items[${index}][quantity]`, String(item.quantity));
      if (item.selling_plan) formData.append(`items[${index}][selling_plan]`, String(item.selling_plan));
      if (!item.properties) return;
      Object.entries(item.properties).forEach(([key, value]) => {
        if (!isUsablePropertyValue(value)) return;
        formData.append(`items[${index}][properties][${key}]`, value);
      });
    });
    const res = await fetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      body: formData
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  };

  const getPaperWrap = (form) => {
    const scope =
      form?.closest('product-form')?.closest('.shopify-section, section, main, body') ||
      form?.closest('.shopify-section, section, main, body') ||
      document;
    return scope.querySelector('[data-paper-addon]');
  };

  const getSelectedPaperVariantId = (form) => {
    const wrap = getPaperWrap(form);
    if (!wrap) return null;

    if (wrap.getAttribute('data-paper-mode') === 'mix') {
      const mode = (wrap.dataset.paperModeState || '').trim();
      if (mode === 'without') return null;
      if (mode !== 'with') return null;
      if (wrap.dataset.paperMixOk !== '1') return null;
      const v = (wrap.dataset.selectedPaperVariantId || '').trim();
      const id = v ? Number(v) : null;
      return Number.isFinite(id) ? id : null;
    }

    const checked = wrap.querySelector('[data-paper-choice]:checked');
    if (checked) {
      const val = (checked.value || '').trim();
      if (!val || val === 'without') return null;
      const id = Number(val);
      return Number.isFinite(id) ? id : null;
    }

    const v = (wrap.dataset.selectedPaperVariantId || wrap.getAttribute('data-selected-paper-variant-id') || '').trim();
    const id = v ? Number(v) : null;
    return Number.isFinite(id) ? id : null;
  };

  const getPaperQty = (form, fallbackQty) => {
    const wrap = typeof form === 'object' && form?.querySelector ? getPaperWrap(form) : document.querySelector('[data-paper-addon]');
    if (!wrap || !wrap.hasAttribute('data-paper-qty')) {
      const fb = parseInt(fallbackQty, 10);
      return Number.isFinite(fb) && fb > 0 ? fb : 1;
    }
    const raw = (wrap.dataset.paperQty || wrap.getAttribute('data-paper-qty') || '').trim();
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n > 0) return n;
    const fb = parseInt(fallbackQty, 10);
    return Number.isFinite(fb) && fb > 0 ? fb : 1;
  };

  const extractMainItemFromForm = (form) => {
    const fd = new FormData(form);
    const id = fd.get('id');
    if (!id) return null;

    const quantity = parseInt(fd.get('quantity') || '1', 10) || 1;

    const properties = {};
    for (const [k, v] of fd.entries()) {
      if (!k.startsWith('properties[')) continue;
      const name = k.slice('properties['.length, -1);
      if (!isUsablePropertyValue(v)) continue;
      properties[name] = v;
    }

    const item = { id: Number(id), quantity };
    if (Object.keys(properties).length) item.properties = properties;

    const sellingPlan = fd.get('selling_plan');
    if (sellingPlan) item.selling_plan = sellingPlan;

    return item;
  };

  const getForProductValue = (form) => {
    const productTitle = (qs('h1')?.textContent || '').trim();
    let selectedOptions = [];

    const formId = form.getAttribute('id') || form.id;
    if (formId) {
      const variantPicker = document.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"]`);
      if (variantPicker) {
        selectedOptions = Array.from(variantPicker.querySelectorAll('[data-selected-option-value]'))
          .map((el) => (el.textContent || '').trim())
          .filter(Boolean);
      }
    }

    if (productTitle && selectedOptions.length) return `${productTitle} - ${selectedOptions.join(' / ')}`;
    return productTitle || '';
  };

  const openCartUI = async () => {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
  };

  const lockKey = '__paper_addon_lock__';

  const acquireLock = () => {
    const now = Date.now();
    const ttl = 1500;
    const cur = window[lockKey];
    if (cur && now - cur < ttl) return false;
    window[lockKey] = now;
    return true;
  };

  const releaseLock = () => {
    window[lockKey] = 0;
  };

  const getMulticolorColors = (form) => {
    const wrap = getPaperWrap(form);
    if (!wrap) return '';
    return (wrap.dataset.multicolorColors || '').trim();
  };

  const getMulticolorPropertyName = (form) => {
    const wrap = getPaperWrap(form);
    const panel = wrap?.querySelector('[data-paper-multicolor-panel]');
    return (panel?.getAttribute('data-property') || 'Multicolor colors').trim() || 'Multicolor colors';
  };

  const isMulticolorComplete = (form) => {
    const wrap = getPaperWrap(form);
    if (!wrap) return true;
    return wrap.dataset.paperMulticolorOk !== '0';
  };

  const isMixMode = (form) => getPaperWrap(form)?.getAttribute('data-paper-mode') === 'mix';

  const isMixComplete = (form) => {
    const wrap = getPaperWrap(form);
    if (!wrap || wrap.getAttribute('data-paper-mode') !== 'mix') return true;
    const mode = (wrap.dataset.paperModeState || '').trim();
    if (mode === 'without') return true;
    if (mode !== 'with') return false;
    return wrap.dataset.paperMixOk === '1';
  };

  const getPaperMixSlots = (form) => {
    const wrap = getPaperWrap(form);
    if (!wrap || wrap.getAttribute('data-paper-mode') !== 'mix') return [];
    if (typeof wrap.__aspGetMixSlots === 'function') {
      return wrap.__aspGetMixSlots().filter((s) => s && Number.isFinite(Number(s.id)) && Number(s.id) > 0);
    }
    return [];
  };

  const getPaperMixGroupedItems = (form) => {
    const slots = getPaperMixSlots(form);
    const map = new Map();
    slots.forEach((s) => {
      const id = Number(s.id);
      if (!Number.isFinite(id) || id <= 0) return;
      const cur = map.get(id) || { id, quantity: 0, title: s.title || '', type: s.type || '' };
      cur.quantity += 1;
      if (!cur.title && s.title) cur.title = s.title;
      if (!cur.type && s.type) cur.type = s.type;
      map.set(id, cur);
    });
    return [...map.values()];
  };

  const getPaperMixPrice = (form) => {
    const wrap = getPaperWrap(form);
    if (!wrap || wrap.getAttribute('data-paper-mode') !== 'mix') return 0;
    if ((wrap.dataset.paperModeState || '').trim() !== 'with') return 0;
    return Number(wrap.dataset.paperMixTotal || 0) || 0;
  };

  const getMixPropertyName = (form) => {
    const wrap = getPaperWrap(form);
    return (wrap?.getAttribute('data-mix-property') || 'Insert mix').trim() || 'Insert mix';
  };

  const getMixOrderedLabel = (form) => {
    const wrap = getPaperWrap(form);
    return (wrap?.dataset.mixOrdered || '').trim();
  };

  const addPaperBundle = async (form) => {
    const mainItem = extractMainItemFromForm(form);
    if (!mainItem) {
      form.submit();
      return;
    }

    const wrap = getPaperWrap(form);
    const mix = wrap?.getAttribute('data-paper-mode') === 'mix';

    if (mix) {
      const mode = (wrap.dataset.paperModeState || '').trim();
      if (mode === 'without') return false;
      if (mode !== 'with' || !isMixComplete(form)) {
        form.dispatchEvent(new CustomEvent('paper:mix-invalid', { bubbles: true }));
        return true;
      }
    } else {
      const paperId = getSelectedPaperVariantId(form);
      if (!paperId) {
        return false;
      }

      if (!isMulticolorComplete(form)) {
        form.dispatchEvent(new CustomEvent('paper:multicolor-invalid', { bubbles: true }));
        return true;
      }
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return true;
    }

    if (!acquireLock()) return false;

    try {
      const forProductValue = getForProductValue(form);
      const bundleToken = `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const mainProperties = { ...(mainItem.properties || {}) };
      mainProperties._bundle_token = bundleToken;
      mainProperties._linked_parent = 'main';
      mainItem.properties = mainProperties;

      const items = [];

      if (mix) {
        const grouped = getPaperMixGroupedItems(form);
        if (!grouped.length) {
          form.dispatchEvent(new CustomEvent('paper:mix-invalid', { bubbles: true }));
          return true;
        }
        const mixLabel = getMixOrderedLabel(form);
        const mixPropName = getMixPropertyName(form);
        grouped.forEach((g) => {
          const paperProperties = { 'For Product': forProductValue, _bundle_token: bundleToken };
          if (mixLabel) paperProperties[mixPropName] = mixLabel;
          items.push({
            id: g.id,
            quantity: g.quantity,
            properties: paperProperties
          });
        });
      } else {
        const paperId = getSelectedPaperVariantId(form);
        const paperProperties = { 'For Product': forProductValue, _bundle_token: bundleToken };
        const multiColors = getMulticolorColors(form);
        if (multiColors) {
          paperProperties[getMulticolorPropertyName(form)] = multiColors;
        }
        items.push({
          id: paperId,
          quantity: getPaperQty(form, mainItem.quantity),
          properties: paperProperties
        });
      }

      if (typeof window.aspSpareElasticLine === 'function') {
        const spare = window.aspSpareElasticLine(bundleToken, forProductValue);
        if (spare) items.push(spare);
      }

      items.push(mainItem);
      await addCartItems(items);

      await openCartUI();
      return true;
    } finally {
      releaseLock();
    }
  };

  window.aspAddPaperBundle = addPaperBundle;
  window.aspGetSelectedPaperVariantId = getSelectedPaperVariantId;
  window.aspGetPaperQty = getPaperQty;
  window.aspIsMulticolorComplete = isMulticolorComplete;
  window.aspIsMixMode = isMixMode;
  window.aspIsMixComplete = isMixComplete;
  window.aspGetPaperMixGroupedItems = getPaperMixGroupedItems;
  window.aspGetPaperMixPrice = getPaperMixPrice;
  window.aspGetMixOrderedLabel = getMixOrderedLabel;
  window.aspGetMixPropertyName = getMixPropertyName;
})();
